import * as layout from '../../../components/markLayout.module.css';
import Section from '@hero/ui-kit/layout/Section';
import MarkModal from '../../../components/MarkModal';
import { MarkButton, MarkHeading } from '../../../components/MarkControls';
import React, { useId } from 'react';
import P from '@hero/ui-kit/typography/P';
import useActivateDrip from '../../../api/useActivateDrip';
import useValidateDrip from '../../../api/useValidateDrip';
import { useNotifications } from '../../../../../context/notification';
import type { DripValidationDestination } from '../../../constants';
import DripValidationMessages from './DripValidationMessages';
import { extractDripErrorCodes } from '../dripFormUtils';
import type { DripDetail } from '../../../types';
type Props = {
    drip: DripDetail;
    onClose: () => void;
    onSuccess: () => void;
    onNavigate: (destination: DripValidationDestination) => void;
};
const ActivateModal: React.FC<Props> = ({ drip, onClose, onSuccess, onNavigate }) => {
    const activate = useActivateDrip();
    const validate = useValidateDrip(drip.drip_id);
    const { addNotification } = useNotifications();
    const headingId = useId();
    const isResume = drip.status === 'PAUSED';
    const promotedDraft = drip.status !== 'DRAFT' ? drip.draft_version : null;
    const heading =
        drip.status === 'DRAFT'
            ? 'Activate drip'
            : isResume
              ? promotedDraft
                  ? `Resume + promote draft v${promotedDraft.version_number}`
                  : 'Resume drip'
              : 'Promote draft';
    const handleConfirm = () => {
        if (activate.isPending || validateBlocking) return;
        activate.mutate(
            { drip_id: drip.drip_id },
            {
                onSuccess: (response) => {
                    addNotification({
                        type: 'success',
                        message: `Activated — version v${response.data.active_version_number}, ${response.data.enrolled_count.toLocaleString()} enrolled.`
                    });
                    onSuccess();
                }
            }
        );
    };
    const repair = (destination: DripValidationDestination) => {
        if (activate.isPending) return;
        onClose();
        onNavigate(destination);
    };
    // Block on any in-flight check (initial or background refetch), a failed
    // check, a missing result, or an explicit invalid — never let Confirm fire
    // against stale or absent validation data.
    const validateLoading = validate.isPending || validate.isFetching;
    const validateBlocking =
        validateLoading ||
        validate.isError ||
        validate.data == null ||
        validate.data.valid === false;
    return (
        <MarkModal
            onClose={onClose}
            isPending={activate.isPending}
            aria-labelledby={headingId}
            styles={{
                width: 'min(64rem, 90vw)'
            }}
        >
            <Section noDefaultPadding className={layout.form}>
                <MarkHeading
                    role="h4"
                    id={headingId}
                    tabIndex={-1}
                    style={{
                        margin: '0 0 1rem'
                    }}
                >
                    {heading}
                </MarkHeading>
                {validateLoading ? (
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{
                            color: 'var(--mark-muted)'
                        }}
                    >
                        Checking validation…
                    </P>
                ) : validate.isError ? (
                    <div role="alert" className={layout.notice}>
                        <DripValidationMessages
                            codes={extractDripErrorCodes(validate.error)}
                            onNavigate={repair}
                            disabled={activate.isPending}
                        />
                        <MarkButton onClick={() => validate.refetch()}>Retry</MarkButton>
                    </div>
                ) : validate.data?.valid === false ? (
                    <div role="alert" className={layout.notice}>
                        <P
                            noDefaultMargin
                            styles={{
                                marginBottom: '0.6rem',
                                color: 'var(--mark-danger)'
                            }}
                        >
                            This drip fails validation. Fix these errors first:
                        </P>
                        <DripValidationMessages
                            codes={validate.data.errors}
                            onNavigate={repair}
                            disabled={activate.isPending}
                        />
                    </div>
                ) : (
                    <>
                        <P
                            noDefaultMargin
                            styles={{
                                marginBottom: '0.6rem'
                            }}
                        >
                            {heading === 'Activate drip' ? 'Activate now?' : `${heading}?`}
                        </P>
                        <ul
                            style={{
                                margin: '0 0 0.8rem',
                                paddingLeft: '1.6rem'
                            }}
                        >
                            {promotedDraft && (
                                <li>
                                    <P noDefaultMargin size="small" strong>
                                        Draft v{promotedDraft.version_number} goes live and replaces
                                        v{drip.current_version?.version_number ?? '—'} for new
                                        enrollments.
                                    </P>
                                </li>
                            )}
                            <li>
                                <P noDefaultMargin size="small">
                                    {isResume
                                        ? 'No enrollment sweep on resume (SEGMENT drips) — profiles that joined the segment while paused are not enrolled by this action.'
                                        : 'Enrollment sweep fires once (SEGMENT drips).'}
                                </P>
                            </li>
                            <li>
                                <P noDefaultMargin size="small">
                                    Mark must be enabled before this drip can be activated.
                                </P>
                            </li>
                            <li>
                                <P noDefaultMargin size="small">
                                    Instances on old versions stay on their version.
                                </P>
                            </li>
                        </ul>
                    </>
                )}
                {activate.error && (
                    <div
                        role="alert"
                        style={{
                            marginTop: '0.8rem'
                        }}
                        className={layout.notice}
                    >
                        <DripValidationMessages
                            codes={extractDripErrorCodes(activate.error)}
                            onNavigate={repair}
                            disabled={activate.isPending}
                        />
                    </div>
                )}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '0.8rem',
                        marginTop: '1.6rem'
                    }}
                >
                    <MarkButton type="button" onClick={onClose} disabled={activate.isPending}>
                        Cancel
                    </MarkButton>
                    <MarkButton
                        type="button"
                        onClick={handleConfirm}
                        disabled={activate.isPending || validateBlocking}
                        variant="primary"
                    >
                        {activate.isPending ? 'Working…' : heading}
                    </MarkButton>
                </div>
            </Section>
        </MarkModal>
    );
};
export default ActivateModal;
